import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Stepper,
  Step,
  StepButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  getCpSourcingSourceUsers,
  getCpSourcingRecipientUsers,
  getEligibleCpsForSourcingManager,
  executeCpSourcingManagerChangeFlow,
} from "../../Services/WorkTransferApis";
import InsightTiles from "../../Components/WorkTransfer/InsightTiles";
import WorkTransferNotice from "../../Components/WorkTransfer/WorkTransferNotice";
import { COLORS, PageHeader, Field, FieldLabel, PrimaryButton, SecondaryButton, fieldSx, cardSx } from "../../Components/WorkTransfer/wtStyle";

// Matches ChannelPartner.jsx's own cpType filter options (Components/
// ChannelPartner/ChannelPartner.jsx) so this reuses the same vocabulary
// instead of inventing a new one.
const CP_TYPE_OPTIONS = ["Entity", "Individual"];

// Reworked 2026-09-01 to match Move Someone's Work's wizard pattern instead
// of one flat card + a ReviewDialog popup — same 3-part shape (pick who ->
// pick what's moving -> recipient + inline review + confirm), just without
// a bucket or approval-transfer step: CPs aren't bucketed by pipeline stage,
// and this mode never touches LeadLogs/approvals at all (cpSourcingManagerFlow.js
// only ever updates CP.cpSourceId). Recipient stays single-only — CP Sourcing
// Manager never supported round robin, unlike Move Work.
//
// Picker data source is UNCHANGED — getCpSourcingSourceUsers/
// getCpSourcingRecipientUsers (both already isCpSourcingUser=true-filtered,
// see cpSourcingManagerFlow.js) — deliberately NOT swapped for Move Work's
// getEligibleSourceUsers/getEligibleRecipientUsers, which target the
// Sales/CRM persona and explicitly EXCLUDE isCpSourcingUser users. Matching
// Move Work's UI style doesn't mean matching its data.
const STEPS = ["Who's CPs are moving", "What's moving", "Recipients & review"];

export default function CpSourcingReassignPage({ canExecute = false }) {
  const [activeStep, setActiveStep] = useState(0);

  const [sourceUsers, setSourceUsers] = useState([]);
  const [recipientUsers, setRecipientUsers] = useState([]);
  const [manager, setManager] = useState(null);
  const [newManager, setNewManager] = useState(null);

  const [search, setSearch] = useState("");
  const [cpTypeFilter, setCpTypeFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [loadingCps, setLoadingCps] = useState(false);
  const [cps, setCps] = useState(null);
  const [selectedCpIds, setSelectedCpIds] = useState([]);

  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    getCpSourcingSourceUsers()
      .then((res) => setSourceUsers(res.success ? res.data.users : []))
      .catch(() => setNotice({ type: "error", title: "Couldn't load managers", message: "Try reloading the page." }));
    getCpSourcingRecipientUsers()
      .then((res) => setRecipientUsers(res.success ? res.data.users : []))
      .catch(() => setNotice({ type: "error", title: "Couldn't load managers", message: "Try reloading the page." }));
  }, []);

  // Same shape as Move Work's sourceOverviewTiles — free, from the already-
  // loaded manager list, no extra query.
  const sourceOverviewTiles = [
    { count: sourceUsers.length, label: "CP sourcing managers", hint: "eligible to hand off CPs", tone: "blue" },
    { count: sourceUsers.filter((u) => u.isActive === false).length, label: "Inactive managers", hint: "blocked accounts — hand these off", tone: "red" },
  ];

  const resetLoadedWork = () => {
    setCps(null);
    setSelectedCpIds([]);
    setNewManager(null);
    setReason("");
  };

  const loadCps = async () => {
    if (!manager) {
      setNotice({ type: "warning", title: "Pick a sourcing manager first", message: "Select the manager whose CPs you're reassigning." });
      return;
    }
    setLoadingCps(true);
    setCps(null);
    setSelectedCpIds([]);
    try {
      const res = await getEligibleCpsForSourcingManager({ userId: manager.userId, cpType: cpTypeFilter, dateFrom, dateTo, search });
      if (!res.success) {
        setNotice({ type: "error", title: "Couldn't load CPs", message: res.message });
        return;
      }
      setCps(res.data.cps);
      if (res.data.cps.length === 0) {
        setNotice({ type: "warning", title: "No CPs found", message: `${manager.name} has no approved CPs matching these filters.` });
      }
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load CPs", message: err.message });
    } finally {
      setLoadingCps(false);
    }
  };

  const toggleCp = (cpId) => {
    setSelectedCpIds((prev) => (prev.includes(cpId) ? prev.filter((id) => id !== cpId) : [...prev, cpId]));
  };

  const allSelected = cps?.length > 0 && selectedCpIds.length === cps.length;
  const someSelected = selectedCpIds.length > 0 && !allSelected;
  const toggleSelectAll = () => {
    setSelectedCpIds(allSelected ? [] : (cps || []).map((c) => c.cpId));
  };

  const goToCpStep = () => {
    if (!cps) return;
    setActiveStep(1);
  };

  const goToRecipientStep = () => {
    if (selectedCpIds.length === 0) {
      setNotice({ type: "warning", title: "Select at least one CP", message: "Tick which CPs are moving to the new sourcing manager." });
      return;
    }
    setActiveStep(2);
  };

  const summaryLines = [
    `Moving ${selectedCpIds.length} of ${cps?.length || 0} CP(s) from ${manager?.name}.`,
    `New CP sourcing manager for the selected CP(s) is ${newManager?.name || "—"}.`,
  ];

  const confirmDisabled = confirming || !canExecute || !newManager || newManager.userId === manager?.userId || reason.trim().length === 0;

  const confirmTransfer = async () => {
    setConfirming(true);
    try {
      const performedBy = JSON.parse(localStorage.getItem("user"))?.userId;
      const res = await executeCpSourcingManagerChangeFlow({
        fromUserId: manager.userId,
        toUserId: newManager.userId,
        cpIds: selectedCpIds,
        reason,
        performedBy,
      });
      if (!res.success) {
        setNotice({ type: "error", title: "Transfer failed", message: res.message });
        return;
      }
      setNotice({ type: "success", title: "CPs reassigned", message: `${res.data.operationsApplied} CP(s) moved.` });
      resetLoadedWork();
      setActiveStep(0);
    } catch (err) {
      setNotice({ type: "error", title: "Transfer failed", message: err.message });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Update CP Sourcing Manager"
        subtitle="A sourcing manager left, or is overloaded — search their CPs and bulk reassign to someone else."
      />

      {/* Stepper capped at 960 so its connector lines don't stretch
          unreadably long on a wide screen (same reasoning as Move Work) —
          everything below it stays full-width instead, so the page doesn't
          leave a large blank gutter on the right like a page-wide 960 cap
          used to (2026-09-01). */}
      <Box sx={{ maxWidth: 960 }}>
        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 3,
            "& .MuiStepLabel-label": { fontSize: 13.5, fontWeight: 600 },
            "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
            "& .MuiStepIcon-root": { fontSize: 26 },
          }}
        >
          {STEPS.map((label, i) => (
            <Step key={label} completed={i < activeStep}>
              <StepButton onClick={() => setActiveStep(i)} disabled={i > 0 && !cps}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      {activeStep === 0 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <InsightTiles tiles={sourceOverviewTiles} />
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Current sourcing manager" sx={{ width: 260 }}>
              <Autocomplete
                options={sourceUsers}
                getOptionLabel={(u) => `${u.name || ""}${u.isActive === false ? " (Inactive)" : ""}`}
                value={manager}
                onChange={(e, val) => {
                  setManager(val);
                  resetLoadedWork();
                }}
                renderInput={(params) => <TextField {...params} placeholder="Choose a manager" />}
                sx={fieldSx}
              />
            </Field>
            <Field label="Search CPs" sx={{ width: 200 }}>
              <TextField placeholder="Name or CP ID" value={search} onChange={(e) => setSearch(e.target.value)} sx={fieldSx} />
            </Field>
            <Field label="CP type" sx={{ width: 200 }}>
              <Autocomplete
                multiple
                options={CP_TYPE_OPTIONS}
                value={cpTypeFilter}
                onChange={(e, val) => setCpTypeFilter(val)}
                renderInput={(params) => <TextField {...params} placeholder="Any type" />}
                sx={fieldSx}
              />
            </Field>
            <Field label="Created from">
              <TextField type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={fieldSx} />
            </Field>
            <Field label="Created to">
              <TextField type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={fieldSx} />
            </Field>
            <PrimaryButton onClick={loadCps} disabled={loadingCps}>
              {loadingCps ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Load CPs"}
            </PrimaryButton>
          </Box>

          {cps && (
            <Typography fontSize={13} color={COLORS.textSecondary}>
              {cps.length} approved CP{cps.length === 1 ? "" : "s"} found for {manager?.name}.
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
            <PrimaryButton onClick={goToCpStep} disabled={!cps}>
              Next →
            </PrimaryButton>
          </Box>
        </Box>
      )}

      {activeStep === 1 && cps && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography fontSize={13} fontWeight={700}>
              CPs — tick who's moving ({selectedCpIds.length}/{cps.length} selected)
            </Typography>
            {cps.length > 0 && (
              <Typography fontSize={12.5} fontWeight={600} sx={{ color: COLORS.blue, cursor: "pointer" }} onClick={toggleSelectAll}>
                {allSelected ? "Clear all" : "Select all"}
              </Typography>
            )}
          </Box>
          {cps.length === 0 ? (
            <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 2 }}>
              No CPs found — go back and adjust the filters.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden" }}>
              {cps.map((c) => (
                <ListItem
                  key={c.cpId}
                  button
                  onClick={() => toggleCp(c.cpId)}
                  sx={{ cursor: "pointer", borderBottom: `1px solid ${COLORS.border}`, "&:last-child": { borderBottom: "none" } }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={selectedCpIds.includes(c.cpId)} onChange={() => toggleCp(c.cpId)} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 14 }}
                    primary={c.billingName}
                    secondaryTypographyProps={{ fontSize: 11.5, color: COLORS.textSecondary }}
                    secondary={[c.cpDisplayId, c.cpType, c.created_at ? new Date(c.created_at).toLocaleDateString() : null].filter(Boolean).join(" · ")}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2.5 }}>
            <SecondaryButton onClick={() => setActiveStep(0)}>← Back</SecondaryButton>
            <PrimaryButton onClick={goToRecipientStep}>Next →</PrimaryButton>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
            New sourcing manager
          </Typography>
          <Field label="Assign CPs to" sx={{ maxWidth: 320, mb: 2 }}>
            <Autocomplete
              options={recipientUsers.filter((u) => u.userId !== manager?.userId)}
              getOptionLabel={(u) => u.name || ""}
              value={newManager}
              onChange={(e, val) => setNewManager(val)}
              renderInput={(params) => <TextField {...params} placeholder="Choose new manager" />}
              sx={fieldSx}
            />
          </Field>

          <Typography fontSize={13} fontWeight={700} sx={{ mt: 1, mb: 1 }}>
            Summary
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
            {summaryLines.map((line, i) => (
              <Typography key={i} fontSize={13.5}>
                • {line}
              </Typography>
            ))}
          </Box>

          <FieldLabel>
            Reason <span style={{ color: COLORS.red }}>*</span>
          </FieldLabel>
          <TextField
            required
            multiline
            minRows={2}
            fullWidth
            placeholder="e.g. Priya resigned, effective 09 Aug 2026"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reason.trim().length === 0}
            helperText={reason.trim().length === 0 ? "A reason is required before this transfer can be confirmed." : " "}
            sx={fieldSx}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mt: 2 }}>
            <SecondaryButton onClick={() => setActiveStep(1)} disabled={confirming}>
              ← Back
            </SecondaryButton>
            <Box sx={{ textAlign: "right" }}>
              <PrimaryButton onClick={() => setConfirmDialogOpen(true)} disabled={confirmDisabled}>
                {confirming ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm transfer"}
              </PrimaryButton>
              {!canExecute && (
                <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1, maxWidth: 260 }}>
                  Your role has view-only access — you can browse this transfer, but can't confirm it.
                </Typography>
              )}
              {canExecute && !newManager && (
                <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1 }}>
                  Choose a new sourcing manager above first.
                </Typography>
              )}
              {canExecute && newManager && newManager.userId === manager?.userId && (
                <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1 }}>
                  The new sourcing manager must be different from the current one.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <WorkTransferNotice notice={notice} onClose={() => setNotice(null)} />

      {/* Same final yes/no checkpoint Move Work uses (2026-08-25) — one
          misclick would otherwise move real CPs with no way back short of
          another manual transfer. */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Confirm this transfer?</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
            {summaryLines.map((line, i) => (
              <Typography key={i} fontSize={13.5}>
                • {line}
              </Typography>
            ))}
          </Box>
          <Typography fontSize={12.5} color={COLORS.textSecondary}>
            This cannot be undone automatically — reassigned CPs would need a separate manual transfer to reverse.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SecondaryButton onClick={() => setConfirmDialogOpen(false)}>No, go back</SecondaryButton>
          <PrimaryButton
            onClick={() => {
              setConfirmDialogOpen(false);
              confirmTransfer();
            }}
          >
            Yes, confirm
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
